import { request } from './apiClient';
import { calcularFcj, calcularFcjEsperado, calcularFcmj, classificarFcj } from '../utils/fcjCalculo';

// Monta a query string a partir de um objeto de filtros, ignorando valores vazios.
function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      search.set(key, value.join(','));
      return;
    }
    search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// Lista os corpos de prova do relatório "Resultados sequencial" (Ensaio de Compressão Axial),
// já com etiqueta/nota fiscal/volume/horários/slump/prensa resolvidos no backend.
// Fcj, Slump Nota Fiscal e Tipo de Capeamento não existem no schema hoje: vêm ausentes/nulos.
export const getResultadoSequencialData = async ({ idsConcretagem, idObra } = {}) => {
  const qs = buildQuery({ idsConcretagem, idObra });
  return await request(`/api/resultadoSequencial${qs}`);
};

// Extrai um nome de exibição de um campo que pode vir como string simples ou como objeto
// relacional (ex.: prensa = { idPrensa, codgIdentificacao }), dependendo do endpoint/registro.
// Sem isso, o objeto bruto acaba indo parar num <Text> do PDF e quebra a geração.
function resolveNome(value, keys) {
  if (value == null) return null;
  if (typeof value === 'string') return value || null;
  for (const key of keys) {
    if (value[key]) return value[key];
  }
  return null;
}

// Normaliza um item de /api/resultadoSequencial para o formato consumido pelo PDF (sequencial e por caminhão).
export function normalizeResultadoSequencial(item) {
  const slump = item.slump || item.Slump || {};
  const resultado = item.resultado || item.Resultado || {};
  const notaFiscal = item.numrNotaFiscalPrincipal || item.numrNotaFiscalSecundaria || '';

  const base = {
    id: String(item.idCorpoProva || item.IdCorpoProva || ''),
    etiqueta: item.etiqueta || item.Etiqueta || item.idenObraEtiqueta || '-',
    notaFiscal: notaFiscal ? String(notaFiscal) : '-',
    volume: item.valrVolume ?? item.ValrVolume ?? null,
    pecaConcretada: item.pecaConcretada || item.PecaConcretada || '-',
    dataSaidaUsina: item.dataSaidaUsina || item.DataSaidaUsina || null,
    dataInicioDescarga: item.dataInicioDescarga || item.DataInicioDescarga || null,
    dataFimDescarga: item.dataFimDescarga || item.DataFimDescarga || null,
    dataMoldagem: item.dataMoldagem || item.DataMoldagem || null,
    dataRuptura: item.dataRuptura || item.DataRuptura || null,
    idadeRuptura: item.idadeRuptura ?? item.IdadeRuptura ?? null,
    slumpAplicacao: slump.valrSlumpFinal ?? slump.valrSlump ?? slump.ValrSlumpFinal ?? slump.ValrSlump ?? null,
    slumpNotaFiscal: null, // campo inexistente no schema hoje
    adicaoAgua: slump.valrAdicaoAgua ?? slump.ValrAdicaoAgua ?? null,
    temperaturaConcreto: item.valrTemperaturaConcreto ?? item.ValrTemperaturaConcreto ?? null,
    valrCarga: resultado.valrCarga ?? resultado.ValrCarga ?? null,
    tipoRuptura: resolveNome(resultado.tipoRuptura || resultado.TipoRuptura, ['nomeTipoRuptura', 'NomeTipoRuptura']),
    prensa: resolveNome(resultado.prensa || resultado.Prensa, ['codgIdentificacao', 'CodgIdentificacao']),
    // dados de cabeçalho, repetidos por item (mesma obra/concretagem em todo o relatório)
    _raw: item,
  };

  const ponderadorTipoCarga = resultado.tipoCarga?.valrPonderador ?? resultado.tipoCarga?.ValrPonderador ?? null;
  const ponderadorTipoCorpoProva = item.tipoCorpoProva?.valrPonderadorCalculo ?? item.tipoCorpoProva?.ValrPonderadorCalculo ?? null;
  const fck = item.valrClasResiCompressao ?? item.ValrClasResiCompressao ?? null;
  const cimento = item.cimento || item.Cimento || null;
  const ponderadorTipoCimento = cimento?.valorPonderador ?? cimento?.ValorPonderador ?? null;

  const fcj = calcularFcj({ valrCarga: base.valrCarga, ponderadorTipoCarga, ponderadorTipoCorpoProva });
  const fcjEsp = calcularFcjEsperado({ fck, idade: base.idadeRuptura, ponderadorTipoCimento });
  const fcmj = calcularFcmj({ fck, idade: base.idadeRuptura, ponderadorTipoCimento });

  return {
    ...base,
    fcj,
    fcjEsp,
    fcmj,
    fck,
    ponderadorTipoCimento,
    corFcj: classificarFcj({ fcj, fcmj, fcjEsperado: fcjEsp }),
  };
}

// Extrai o id da concretagem de origem de um item bruto de /api/resultadoSequencial.
// AJUSTE BACKEND AQUI: campo/relação usado ainda não confirmado no contrato da API; ajustar
// a lista de chaves abaixo se o backend expuser o vínculo com outro nome.
function extractIdConcretagem(item) {
  const raw = item?._raw || item;
  const value = raw?.concretagemRealizada?.idConcretagemRealizada
    ?? raw?.idConcretagemRealizada
    ?? raw?.id_concretagem_realizada
    ?? raw?.idConcretagem
    ?? '';
  return value ? String(value) : '';
}

// Retorna o Set de ids de concretagem que têm ao menos um corpo de prova com resultado
// (usado para só listar, na tela, datas que realmente permitem gerar o relatório).
export async function getConcretagensComResultado({ idsConcretagem, idObra } = {}) {
  if (!idsConcretagem || idsConcretagem.length === 0) return new Set();

  const data = await getResultadoSequencialData({ idsConcretagem, idObra });
  const list = Array.isArray(data) ? data : data?.items || [];
  const resultados = list.map(normalizeResultadoSequencial).filter((item) => item.id);

  const ids = resultados.map(extractIdConcretagem).filter(Boolean);
  return new Set(ids);
}

// Monta os dados de cabeçalho do PDF a partir do primeiro item retornado.
export function buildCabecalho(items) {
  const first = items[0]?._raw;
  if (!first) return {};

  const obra = first.obra || first.Obra || {};
  const empresa = obra.empresa || obra.Empresa || {};
  const contato = obra.contato || obra.Contato || {};
  const fck = first.valrClasResiCompressao ?? first.ValrClasResiCompressao;
  const cimento = first.cimento || first.Cimento || null;

  return {
    cliente: empresa.razaoSocial || empresa.RazaoSocial || '-',
    obra: obra.nomeObra || obra.NomeObra || '-',
    contato: contato.nomeCompleto || contato.NomeCompleto || '-',
    email: contato.enderecoEmail || contato.EnderecoEmail || '-',
    cnpj: empresa.numrCnpj || empresa.numr_cnpj || empresa.cnpj || '-',
    endereco: empresa.endereco || empresa.Endereco || '-',
    telefone: contato.telefone || contato.numrTelefone || contato.numr_telefone || '-',
    local: obra.nomeCidade || obra.NomeCidade || '-',
    cimento: cimento?.nomeTipoCimento ?? cimento?.NomeTipoCimento ?? '-',
    fck: fck != null ? String(fck) : '-',
    tipoConcreto: resolveNome(first.tipoConcreto || first.TipoConcreto, ['nomeTipoConcreto', 'NomeTipoConcreto']) || '-',
    tipoCorpoProva: first.tipoCorpoProva?.nomeTipoCorpoProva || first.tipoCorpoProva?.NomeTipoCorpoProva || '-',
    concreteira: resolveNome(first.concreteira || first.Concreteira, ['nomeConcreteira', 'NomeConcreteira']) || '-',
    tipoCapeamento: '-', // campo inexistente no schema hoje
  };
}
